/**
 * Playlist Schedule Filter (V067)
 *
 * Decides if a PlaylistItem can play RIGHT NOW based on the schedule(s) of the
 * source playlist(s) that originated the item. Mirrors `groupScheduleFilter`
 * (V060) EXACTLY, reusing the same `scheduleParser.ts` primitives (preserves
 * Tizen hour=24 normalization — v1.0.105+).
 *
 * Architecture (decisão Renato — Socratic Gate V067):
 *
 *   Server (`MediaSyncService`):
 *     Anexa (campanha atribuída DIRETO ao player, fora de playlist) → item SEM
 *     `via_playlists` (vazio/ausente).
 *     Campanha via playlist → `via_playlists=[id1, id2]` no item.
 *     SyncResponse traz `playlists[]` com a janela de cada playlist de origem
 *     (mesmo formato de schedule de Conteúdo/Campanha/Grupo, MAS sem
 *     `schedule_mode` — `tab_playlist` não tem a coluna).
 *
 *   Cliente (este arquivo):
 *     - BYPASS: item sem `viaPlaylists` (anexa) → IGNORA a janela da playlist.
 *       Operador atribuiu a campanha direto ao player, fora de qualquer
 *       playlist → não há janela de playlist a respeitar.
 *     - UNIÃO: `viaPlaylists=[id1, id2]` → toca se QUALQUER playlist
 *       referenciada permitir o horário atual (mais permissivo).
 *     - Fail-open: `viaPlaylists` apontando pra playlist que NÃO está em
 *       `playlists[]` (caso degradado) → trata como sem restrição.
 *     - Inferência de modo: SEM `scheduleMode` no DTO. Se `weeklySchedule`
 *       presente e não-vazio → ADVANCED (per-day); caso contrário → SIMPLE
 *       (`startAt`/`endAt` + `daysOfWeek`).
 *
 *   Composição AND completa no player (6 camadas):
 *     Conteúdo (V059) ∩ PlaylistItem ∩ Campanha ∩ Grupo (V060) ∩ Player (V066) ∩
 *     Playlist (V067)
 */

import { PlaylistItem, PlaylistSchedule } from '../types';
import {
  getCurrentTimeInTimezone,
  isDayActive,
  isTimeInRange,
} from './scheduleParser';

const WEEKDAY_CODE_TO_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const WEEKDAY_INDEX_TO_CODE: Record<number, string> = {
  0: 'SUN',
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
};

const DEFAULT_TZ = 'America/Sao_Paulo';

export interface PlaylistScheduleDecision {
  /** true if item can play RIGHT NOW under playlist window rules. */
  isPlayable: boolean;
  /** null if isPlayable=true; otherwise human-friendly reason (debug/log). */
  reason: string | null;
  /** Playlist id(s) that PERMITIRAM a passagem (any-pass na UNIÃO). Pode ser []
   *  quando isPlayable=true por BYPASS (anexa) ou por fail-open. */
  passedByPlaylists: number[];
}

function normalizeWeekdayCode(raw: string): string {
  return raw.trim().toUpperCase().slice(0, 3);
}

/**
 * Same SIMPLE/ADVANCED machinery used pra Conteúdo (V059)/Campanha/Grupo (V060)
 * — mas aplicada a uma única playlist.
 *
 * <p><b>Inferência de modo</b> (V067): `tab_playlist` NÃO tem `schedule_mode`.
 * Se `weeklySchedule` presente e não-vazio → ADVANCED (per-day); caso contrário
 * → SIMPLE (`startAt`/`endAt` + `daysOfWeek`).</p>
 *
 * <p>`validFrom`/`validUntil` avaliados aqui no fuso LOCAL do device (decisão
 * Renato — R0 em regras-temporais-player.md). Playlist definida com "até 12/06"
 * expira no fim do dia 12/06 LOCAL do player, não em BRT.</p>
 */
export function isPlaylistCurrentlyOpen(
  schedule: PlaylistSchedule,
  now: Date = new Date(),
): { open: boolean; reason: string | null } {
  // EARLY RETURN — validade no fuso LOCAL do device (vide R0).
  if (schedule.validFrom) {
    const start = parseLocalStartOfDay(schedule.validFrom);
    if (start && now < start) {
      return {
        open: false,
        reason: `validFrom ${schedule.validFrom} ainda no futuro (fuso local)`,
      };
    }
  }
  if (schedule.validUntil) {
    const end = parseLocalEndOfDay(schedule.validUntil);
    if (end && now > end) {
      return {
        open: false,
        reason: `validUntil ${schedule.validUntil} já passou (fuso local)`,
      };
    }
  }

  // INFERÊNCIA DE MODO (V067) — sem schedule_mode no DTO.
  const weekly = schedule.weeklySchedule;
  const isAdvanced = !!weekly && Object.keys(weekly).length > 0;

  // ADVANCED (inferido) — uses weeklySchedule
  if (isAdvanced) {
    const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
      DEFAULT_TZ,
      now,
    );
    const todayCode = WEEKDAY_INDEX_TO_CODE[dayOfWeek];
    const dayConfig = weekly![todayCode];
    if (!dayConfig) {
      return {
        open: false,
        reason: `dia ${todayCode} não configurado em weeklySchedule`,
      };
    }
    try {
      const inRange = isTimeInRange(
        { hours, minutes },
        dayConfig.startAt,
        dayConfig.endAt,
      );
      return {
        open: inRange,
        reason: inRange
          ? null
          : `hora ${hours}:${minutes} fora de ${dayConfig.startAt}-${dayConfig.endAt} (${todayCode})`,
      };
    } catch (err) {
      return {
        open: false,
        reason: `weeklySchedule[${todayCode}] inválido: ${(err as Error).message}`,
      };
    }
  }

  // SIMPLE (inferido, default) — daysOfWeek (lowercase) + startAt/endAt.
  const daysRaw = schedule.daysOfWeek;
  const startAt = schedule.startAt;
  const endAt = schedule.endAt;

  const hasDayFilter = daysRaw && daysRaw.length > 0;
  const hasTimeFilter =
    !!startAt && !!endAt && !(startAt === '00:00' && endAt === '23:59');

  // Playlist sem nenhum filtro = sempre aberta (sem restrição horária definida).
  if (!hasDayFilter && !hasTimeFilter) {
    return { open: true, reason: null };
  }

  const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
    DEFAULT_TZ,
    now,
  );

  if (hasDayFilter) {
    const dayIndices = daysRaw!
      .map((d) => WEEKDAY_CODE_TO_INDEX[normalizeWeekdayCode(d)])
      .filter((idx) => idx !== undefined);
    if (!isDayActive(dayOfWeek, dayIndices)) {
      const todayCode = WEEKDAY_INDEX_TO_CODE[dayOfWeek];
      return {
        open: false,
        reason: `dia ${todayCode} ∉ daysOfWeek=[${daysRaw!.join(',')}]`,
      };
    }
  }

  if (hasTimeFilter) {
    try {
      const inRange = isTimeInRange({ hours, minutes }, startAt!, endAt!);
      if (!inRange) {
        return {
          open: false,
          reason: `hora ${hours}:${minutes} fora de ${startAt}-${endAt}`,
        };
      }
    } catch (err) {
      return {
        open: false,
        reason: `startAt/endAt inválido: ${(err as Error).message}`,
      };
    }
  }

  return { open: true, reason: null };
}

/**
 * Decide se um item pode tocar AGORA respeitando regras de playlist (V067).
 *
 * Ordem de avaliação (espelha groupScheduleFilter V060):
 *  1. BYPASS: item sem `viaPlaylists` (anexa) → SEMPRE toca (ignora playlists).
 *  2. Tem `viaPlaylists`: UNIÃO — toca se QUALQUER playlist referenciada
 *     permitir.
 *  3. Fail-open: se nenhuma das `viaPlaylists` está em `playlists[]`, trata
 *     como sem restrição (caso degradado — sync inconsistente).
 */
export function isPlaylistSchedulePlayable(
  item: PlaylistItem,
  playlists: PlaylistSchedule[] | null | undefined,
  now: Date = new Date(),
): PlaylistScheduleDecision {
  const viaPlaylists = item.viaPlaylists;

  // 1. BYPASS — anexa (sem viaPlaylists): campanha atribuída direto ao player,
  // fora de playlist. Não há janela de playlist a respeitar.
  if (!viaPlaylists || viaPlaylists.length === 0) {
    return {
      isPlayable: true,
      reason: null,
      passedByPlaylists: [],
    };
  }

  // 2+3. Tem viaPlaylists: localiza os schedules e aplica UNIÃO.
  const playlistsById = new Map<number, PlaylistSchedule>();
  if (playlists) {
    for (const p of playlists) {
      playlistsById.set(p.id, p);
    }
  }

  const knownPlaylists = viaPlaylists
    .map((id) => playlistsById.get(id))
    .filter((p): p is PlaylistSchedule => !!p);

  // Fail-open: viaPlaylists referencia playlists não-presentes no sync.
  if (knownPlaylists.length === 0) {
    return {
      isPlayable: true,
      reason: `fail-open (viaPlaylists=[${viaPlaylists.join(',')}] não estão em playlists)`,
      passedByPlaylists: [],
    };
  }

  // UNIÃO: toca se PELO MENOS UMA playlist estiver aberta agora.
  const passReasons: string[] = [];
  const passedByPlaylists: number[] = [];
  for (const p of knownPlaylists) {
    const { open, reason } = isPlaylistCurrentlyOpen(p, now);
    if (open) {
      passedByPlaylists.push(p.id);
    } else {
      passReasons.push(
        `playlist#${p.id}${p.name ? `(${p.name})` : ''}: ${reason}`,
      );
    }
  }

  if (passedByPlaylists.length > 0) {
    return {
      isPlayable: true,
      reason: null,
      passedByPlaylists,
    };
  }

  return {
    isPlayable: false,
    reason: `nenhuma playlist aberta agora — ${passReasons.join(' | ')}`,
    passedByPlaylists: [],
  };
}

/**
 * 2026-06-12 — Parse "YYYY-MM-DD" no fuso LOCAL do device. Helpers
 * compartilhados com `groupScheduleFilter.ts`/`playerScheduleFilter.ts`
 * (mesma lógica, copiados pra manter cada filter self-contained).
 */
function parseLocalStartOfDay(dateStr: string): Date | null {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
}

function parseLocalEndOfDay(dateStr: string): Date | null {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999);
}
